import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { VIBE_CHAT_API_BASE } from '@/lib/vibeChatConfig';

/**
 * Tracks whether GET /chat/{sessionId}/prd returns a document (S3/local).
 * Probes GET /prd per session; resets when sessionId changes.
 */
export function useChatPrdAvailable(sessionId, prdAvailableHint = false) {
  const [prdAvailable, setPrdAvailable] = useState(false);
  const [prdChecking, setPrdChecking] = useState(false);

  const markPrdAvailable = useCallback(() => {
    setPrdAvailable(true);
  }, []);

  useEffect(() => {
    setPrdAvailable(Boolean(prdAvailableHint));
  }, [sessionId, prdAvailableHint]);

  useEffect(() => {
    setPrdChecking(false);
    if (!sessionId || !VIBE_CHAT_API_BASE) {
      setPrdAvailable(false);
      return undefined;
    }

    const base = VIBE_CHAT_API_BASE.replace(/\/$/, '');
    const url = `${base}/chat/${sessionId}/prd`;
    const probeSessionId = sessionId;
    let cancelled = false;

    const probe = async () => {
      setPrdChecking(true);
      try {
        const { status } = await axios.get(url, {
          responseType: 'arraybuffer',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          },
          timeout: 60000,
          validateStatus: (s) => s === 200 || s === 404,
        });
        if (cancelled || probeSessionId !== sessionId) return;
        setPrdAvailable(status === 200);
      } catch {
        if (!cancelled && probeSessionId === sessionId) {
          setPrdAvailable(false);
        }
      } finally {
        if (!cancelled && probeSessionId === sessionId) {
          setPrdChecking(false);
        }
      }
    };

    void probe();
    const timer = setInterval(probe, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [sessionId]);

  return { prdAvailable, prdChecking, markPrdAvailable };
}

/** Show PRD artifact panel for sessions that reached or passed confirmation. */
export function shouldShowPrdArtifact(phase, prdAvailable = false) {
  if (prdAvailable) return true;
  const p = String(phase || '').toUpperCase();
  return ['CONFIRM', 'GENERATE', 'DONE'].includes(p);
}
